import React, { createContext, useContext, useEffect, useState } from "react";

export interface OrderItem {
  _id?: string;
  itemId: string;
  name: string;
  kitchenName?: string;
  cartId: string;
  price: number;
  quantity: number;
  categoryId: string
  tax:number
  gst?: number;
  pst?: number;
  discountAmount?: string;
  imageUrl?: string;
  customOptions?: { label: string; price: number; qty: number; groupName?: string; }[];
  /** Deal claim this line belongs to, and the deal's name (receipts / kitchen) */
  dealId?: string;
  dealName?: string;
  isPartOfDeal?: boolean;
}

interface OrderData {
  items?: OrderItem[];
  totalAmount?: string;
  discountAmount?: string;
  phoneNumber?: string;
  tableToken?: number | string;
  customOptions?: { label: string; price: number; qty: number }[];
  /** Deals on this order: dealId = claim id, originalDealId = the deal */
  appliedDeals?: { dealId: string; originalDealId?: string; name: string; discountAmount: number }[];
}

interface UserData {
  id: string;
  name: string;
  email: string;
  role: string;
  restaurantId?: string;
}

interface OrderContextType {
  orderData: OrderData | null;
  setOrderData: React.Dispatch<React.SetStateAction<OrderData | null>>;
  clearOrder: () => void;
  user: UserData | null;
  setUser: React.Dispatch<React.SetStateAction<UserData | null>>;
  logoUrl: string;
  setLogoUrl: React.Dispatch<React.SetStateAction<string>>;
  clearUser: () => void;
}

export const EmpOrderContext = createContext<OrderContextType | undefined>(
  undefined,
);

export const EmpOrderProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [user, setUser] = useState<UserData | null>(null);
  const [logoUrl, setLogoUrl] = useState<string>(() => {
    if (typeof window === "undefined") return "";
    return window.sessionStorage.getItem("emp_logo_url") || "";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (logoUrl) {
      window.sessionStorage.setItem("emp_logo_url", logoUrl);
    } else {
      window.sessionStorage.removeItem("emp_logo_url");
    }
  }, [logoUrl]);

  const clearOrder = () => setOrderData(null);
  const clearUser = () => setUser(null);

  return (
    <EmpOrderContext.Provider value={{ 
      orderData, 
      setOrderData, 
      clearOrder,
      logoUrl,
      setLogoUrl,
      user,
      setUser,
      clearUser
    }}>
      {children}
    </EmpOrderContext.Provider>
  );
};

export const useOrder = () => {
  const context = useContext(EmpOrderContext);
  if (!context) {
    throw new Error("Error creating order via context");
  }
  return context;
};

export const useEmpOrderOptional = () => useContext(EmpOrderContext);
